import * as React from 'react'
import Head from 'next/head'
import { useAmp } from 'next/amp'
import getConfig from 'next/config'

import gql from 'graphql-tag'
import { ApolloClient, NormalizedCacheObject } from 'apollo-boost'

import {
  ContentHeaderLayout,
  withCacheConfiguration,
} from '@thg-commerce/enterprise-core'
import {
  EnterpriseNextPageContext,
  EnterpriseNextPage,
} from '@thg-commerce/enterprise-core/src/App/types'
import {
  ContentSlug,
  ContentSlugType,
  NetworkClient,
  RootQueryContentSlugArgs,
  SeoConfig,
} from '@thg-commerce/enterprise-network'

import CustomError from '../_error'
import { ArticleData, ArticlePage, CategoryPage } from '../../src/components'
import { Post, Category, Tag, PostAuthor as User } from '../../src/types'
import { postValidator } from '../../src/validator'
import { getInfoFromQueryParam, getPaginationVaribles } from '../../src/utils'

export const POSTS_PER_PAGE = 15

const CONTENT_SLUG_QUERY = gql`
  query contentSlug(
    $path: String!
    $categoryLimit: Int!
    $categoryOffset: Int!
    $postLimit: Int!
    $postOffset: Int!
  ) {
    contentSlug(
      path: $path
      categoryLimit: $categoryLimit
      categoryOffset: $categoryOffset
      postLimit: $postLimit
      postOffset: $postOffset
    ) @client {
      type
      data {
        ... on Post {
          titleDecoded
          date
          content
          slug
          postTemplate
          link
          uri
          postSettings {
            headerType
            headerHeroImage {
              mediaItemUrl
            }
          }
          featuredImage {
            mediaItemUrl
            mediaDetails {
              height
              width
            }
          }
          categories {
            nodes {
              name
              slug
              uri
            }
          }
          postAuthorData {
            subheader
            bio
            image
            name
            slug
            is_ugc
          }
          author {
            slug
            uri
          }
          seo {
            appStoreId
            iTunesAffiliateCode
            canonical
            metaDesc
            metaKeywords
            metaRobotsNofollow
            metaRobotsNoindex
            opengraphAuthor
            opengraphDescriptionDecoded
            opengraphModifiedTime
            opengraphPublishedTime
            opengraphPublisher
            opengraphSiteName
            opengraphTitle
            opengraphType
            opengraphUrl
            schema {
              articleType
              pageType
            }
            title
            twitterDescription
            twitterTitle
            twitterExtraFields {
              twitterCardType
              twitterSite
            }
            structuredDataJsonObject
            customBreadcrumbs {
              path
              text
            }
          }
          citations {
            citations
          }
          postRelatedPostsData {
            data {
              ... on Post {
                slug
                uri
                titleDecoded
                date
                featuredImage {
                  mediaItemUrl
                }
                author {
                  slug
                  uri
                }
                postAuthorData {
                  name
                }
                categories {
                  nodes {
                    name
                    slug
                    uri
                  }
                }
              }
            }
          }
          tesseractWidgetData {
            top10Widget {
              description
              link
              sku
              title
            }
            productWidget {
              sku
              type
              widgetNumber
            }
          }
          signoff {
            signoff
          }
        }
        ... on Category {
          name
          uri
          parent {
            name
            uri
          }
          seo {
            title
            structuredDataJsonObject
            metaDesc
          }
          categoryImage {
            headerType
            headerImage
            featuredImage {
              desktop
              mobile
            }
          }
          childCategories(limit: $categoryLimit, offset: $categoryOffset) {
            name
            uri
            categoryImage {
              featuredImage {
                desktop
                mobile
              }
            }
          }
          childPosts(limit: $postLimit, offset: $postOffset) {
            uri
            titleDecoded
            date
            featuredImage {
              mediaItemUrl
            }
            author {
              uri
            }
            postAuthorData {
              name
              slug
              is_ugc
            }
            postCategories {
              name
              uri
            }
          }
          count
        }
        ... on Tag {
          name
          uri
          seo {
            title
            structuredDataJsonObject
          }
          childPosts(limit: $postLimit, offset: $postOffset) {
            uri
            titleDecoded
            date
            featuredImage {
              mediaItemUrl
            }
            author {
              uri
            }
            postAuthorData {
              name
              slug
              is_ugc
            }
            postCategories {
              name
              uri
            }
          }
          count
        }
        ... on User {
          username: name
          description
          profilePictureUrl
          jobTitle
          uri
          structuredDataJsonObject
          userData {
            subheader
          }
          avatar {
            url
          }

          seo {
            title
          }
          childPosts(limit: $postLimit, offset: $postOffset) {
            uri
            titleDecoded
            date
            featuredImage {
              mediaItemUrl
            }
            author {
              uri
            }
            postAuthorData {
              name
              slug
              is_ugc
            }
            postCategories {
              name
              uri
            }
          }
          count
        }
      }
    }
    seo @client {
      breadcrumbs {
        enabled
      }
    }
  }
`

type SlugPageData = ArticleData | Category | Tag | User | null
export interface SlugPageProps {
  type: ContentSlugType | null
  data: SlugPageData
  page?: number
  hasWebP: boolean
}

const Slug: EnterpriseNextPage<SlugPageProps> = (props) => {
  const { type, data, page } = props
  const isAmp = useAmp()

  if (!type || !data) {
    return (
      <React.Fragment>
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
        <CustomError statusCode={404} />
      </React.Fragment>
    )
  }

  if (type === ContentSlugType.Post) {
    const { post, content, seoConfig } = data as ArticleData
    return (
      <ArticlePage
        isAmp={isAmp}
        hasWebP={props.hasWebP}
        post={post}
        content={content}
        seoConfig={seoConfig}
      />
    )
  }
  if (Object.values(ContentSlugType).includes(type) && page) {
    return (
      <CategoryPage
        isAmp={isAmp}
        category={data as Category | Tag}
        page={page}
        hasWebP={props.hasWebP}
      />
    )
  }

  return (
    <React.Fragment>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      <CustomError statusCode={404} />
    </React.Fragment>
  )
}

Slug.Layout = ContentHeaderLayout

Slug.useAmpLayout = true

// @ts-ignore - @TODO: REBUILD-4781 update to use getServerSideProps
Slug.getInitialProps = withCacheConfiguration<SlugPageProps>(
  async (ctx: EnterpriseNextPageContext) => {
    if (ctx) {
      const { query, res } = ctx
      const apolloClient = ctx.apolloClient as ApolloClient<
        NormalizedCacheObject
      >

      if (query.slug) {
        const { slug: slugToQuery, page } = getInfoFromQueryParam(query.slug)

        const categoryLimit = 0 // in tesseract 0 means no limit, currently we display all child categories regardless of amount
        const categoryOffset = 0

        const { offset: postOffset, limit: postLimit } = getPaginationVaribles(
          page || 1,
          POSTS_PER_PAGE,
        )

        const { data } = await apolloClient.query<
          { contentSlug: ContentSlug; seo: SeoConfig },
          RootQueryContentSlugArgs & {
            categoryLimit: number
            categoryOffset: number
            postLimit: number
            postOffset: number
          }
        >({
          query: CONTENT_SLUG_QUERY,
          variables: {
            categoryLimit,
            categoryOffset,
            postLimit,
            postOffset,
            path:
              query.slug[0] === 'tag'
                ? `${query.slug[0]}/${slugToQuery}`
                : slugToQuery,
          },
          context: { clientName: NetworkClient.Content },
        })

        if (!data || !data?.contentSlug?.type || !data?.contentSlug?.data) {
          if (res) res.statusCode = 404
          return { type: null, data: null, hasWebP: false }
        }

        if (data.contentSlug.type === ContentSlugType.Post) {
          // This validation is temporary until work is complete on REBUILD-876, to prevent the need for complicated code when rendering
          const post =
            data?.contentSlug.data &&
            postValidator.isValidSync(data.contentSlug.data)
              ? (data.contentSlug.data as Post)
              : null
          const seoConfig = data?.seo ? data.seo : null

          if (!post) {
            if (res) res.statusCode = 404
            return { type: null, data: null, hasWebP: false }
          }

          try {
            const content = JSON.parse(post.content)
            const ajv = new (require('ajv').default)()
            const validate = ajv.compile(
              require(`${__dirname}/../../post-content.schema.json`),
            )

            if (!validate(content)) {
              if (!getConfig().publicRuntimeConfig.IS_PRODUCTION) {
                console.error(
                  'Article content validation failed with errors:',
                  validate.errors,
                )
              }
              throw Error('Failed to validate article content')
            }

            return {
              type: data.contentSlug.type,
              data: { post, content, seoConfig },
              hasWebP:
                (ctx?.req?.headers?.hasOwnProperty('accept') &&
                  ctx?.req?.headers?.accept?.includes('image/webp')) ||
                false,
            }
          } catch (e) {
            if (res) res.statusCode = 404
            ctx.logger.error(
              `An error occured whilst validation article (${query.slug}): ${e.message}`,
            )
          }
        }

        if (data.contentSlug.type === ContentSlugType.User) {
          const userData: User = data.contentSlug.data as User
          userData.name = userData.username

          return {
            type: data.contentSlug.type,
            page: page || 1,
            data: userData,
            hasWebP:
              (ctx?.req?.headers?.hasOwnProperty('accept') &&
                ctx?.req?.headers?.accept?.includes('image/webp')) ||
              false,
          }
        }
        if (
          data.contentSlug.type !== ContentSlugType.Post &&
          Object.values(ContentSlugType).includes(data.contentSlug.type)
        ) {
          return {
            cache: {
              ttl: 300,
              grace: 3600,
            },
            type: data.contentSlug.type,
            page: page || 1,
            data: data.contentSlug.data as Category | Tag | User,
            hasWebP:
              (ctx?.req?.headers?.hasOwnProperty('accept') &&
                ctx?.req?.headers?.accept?.includes('image/webp')) ||
              false,
          }
        }
      }
    }

    return { type: null, data: null, hasWebP: false }
  },
)

export default Slug
